import { useEffect, useMemo, useState } from 'react';
import { getPlatformRates } from '../../features/finance/api';
import {
  UsdRatesMap,
  buildRatesMap,
  decimalToUsd,
  unitsToUsd,
} from '../utils/cryptoUsd';

let cachedRates: UsdRatesMap | null = null;
let loadPromise: Promise<UsdRatesMap> | null = null;

const loadRates = (): Promise<UsdRatesMap> => {
  if (cachedRates) return Promise.resolve(cachedRates);
  if (!loadPromise) {
    loadPromise = getPlatformRates()
      .then((data) => {
        cachedRates = buildRatesMap(data.rates);
        return cachedRates;
      })
      .catch(() => {
        loadPromise = null;
        return {};
      });
  }
  return loadPromise;
};

export const usePlatformUsdRates = () => {
  const [rates, setRates] = useState<UsdRatesMap>(cachedRates ?? {});
  const [isLoading, setIsLoading] = useState(cachedRates === null);

  useEffect(() => {
    if (cachedRates) {
      setRates(cachedRates);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    loadRates().then((map) => {
      if (!cancelled) {
        setRates(map);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const helpers = useMemo(
    () => ({
      toUsdUnits: (units: number, currency: string) => unitsToUsd(units, currency, rates),
      toUsdDecimal: (amount: number, currency: string) =>
        decimalToUsd(amount, currency, rates),
    }),
    [rates]
  );

  return { rates, isLoading, ...helpers };
};
